/**
 * src/state/actions/useIncidentActions.js
 *
 * Incident-domain action callbacks.
 *
 * Incidents and IR exercises persist through the dedicated incidents-write
 * per-record store (ADR-0007, INA-03) instead of the whole-tenant-state blob.
 * Two people editing DIFFERENT incidents now touch two different rows and
 * cannot false-conflict, and a save based on a version another tab has already
 * replaced is refused with a 409 the user is shown rather than silently
 * applied on top of it (#4846's two-tabs-same-incident case, at the new
 * grain). It also takes incidents off the single biggest write in the product
 * — ADR-0007 §1.2's write-amplification argument, the #5704 -> #5845 chain.
 *
 * Same optimistic-update-then-reconcile shape as useBcdrActions: mutate local
 * state first so the UI stays immediate, then reconcile with what the server
 * actually committed — or roll the record back, because a refused write is
 * never rescued by the debounced blob save once the store is authoritative.
 *
 * Incident PROPOSALS are not touched here. They live in their own store and
 * are accepted/dismissed through src/api/incidentProposalApi.js (INC-B-002).
 *
 * Sub-hook: useIncidentActions({ updateState, tenantId })
 *
 * Returns:
 *   saveIncident, deleteIncident,
 *   saveIncidentExercise, deleteIncidentExercise
 *
 * Every save/delete below is async — it resolves once the server has
 * acknowledged the write (or rejects, with the optimistic local update already
 * rolled back). Callers must await it (or handle the rejection) to know the
 * write actually landed — see usePromiseSaveStatus.js for the shared UI
 * pattern the incident modals use (STG-B-003).
 */

import { useCallback, useRef } from 'react';
import { now } from '../../utils/formatting.js';
import { logActivity } from '../../api/activityLogger.js';
import { emitEngineEvent } from '../../engine/engineEventBus.js';
import * as incidentApi from '../../api/incidentApi.js';
import {
  captureRecord,
  restoreRecord,
  beginRecordWrite,
  markRecordCommitted,
  committedSinceFence,
} from './optimisticRollback.js';
import { queueNetworkWrite } from '../networkRetryQueue.js';

export function useIncidentActions({ updateState, tenantId }) {
  // This tab's own saves of one record, in order (GH #7018). The screen hands
  // each save the record's rowVersion as last reconciled, so a second save
  // started while the first is in flight carries the version the first is
  // about to replace — and the server refused it with 409: the tab conflicting
  // with itself (e.g. "Log DPO consultation" then an Art. 34 notification).
  // A save now waits for the previous one on the same record and, when it was
  // based on a version this tab's own chain has moved past, is rebased onto
  // the version that write committed. A version this tab never held is left
  // alone, so another tab's edit still 409s exactly as before.
  const chainsRef = useRef(new Map());

  /**
   * Build a save/delete pair for one incident collection. Both collections
   * share an identical optimistic-update-then-reconcile shape, so a factory
   * avoids two near-duplicate hand-written closures while keeping each
   * collection's behaviour traceable through one function (useBcdrActions'
   * makeCollectionActions, same reasoning).
   *
   *   stateKey     — state.<key> array this collection lives under
   *   bodyKey      — the field name incidents-write's response carries the
   *                  record under (matches azure-functions/incidents-write's
   *                  COLLECTIONS config exactly)
   *   saveFn       — incidentApi.saveXxxRecord
   *   deleteFn     — incidentApi.deleteXxxRecord
   *   labelOf      — (record) => the activity-log recordLabel
   *   onSaved      — optional side effect once a save has landed (engine event)
   *   onDeleted    — optional side effect once a delete has landed
   */
  function makeCollectionActions({
    stateKey,
    bodyKey,
    saveFn,
    deleteFn,
    labelOf,
    onSaved,
    onDeleted,
  }) {
    const chains = chainsRef.current;
    const save = async (record, session, options) => {
      let isEdit = false;
      let before = null;
      const fenceKey = `${stateKey}:${record.id}`;
      const fence = beginRecordWrite(fenceKey);

      // createdAt/updatedAt stamping is unchanged from the pre-migration
      // behaviour — the same two lines, moved from inside the updateState
      // mutator to here so the record posted to the server and the record
      // shown optimistically are byte-identical.
      const ts = now();
      const stamped = { ...record, updatedAt: ts };
      if (!stamped.createdAt) stamped.createdAt = ts;

      updateState((s) => {
        if (!s[stateKey]) s[stateKey] = [];
        const idx = s[stateKey].findIndex((r) => r && r.id === stamped.id);
        isEdit = idx >= 0;
        before = captureRecord(s[stateKey], stamped.id);
        if (isEdit) s[stateKey][idx] = stamped;
        else s[stateKey] = [stamped, ...s[stateKey]];
      });

      // Join this record's chain. `own` is every version this tab's chain has
      // been based on or produced; it is a live set, read when the next save
      // decides whether to rebase.
      const prior = chains.get(fenceKey) || null;
      let settle;
      const entry = {
        own: new Set(prior ? prior.own : []),
        committed: null,
        settled: new Promise((resolve) => {
          settle = resolve;
        }),
      };
      if (record.rowVersion) entry.own.add(record.rowVersion);
      chains.set(fenceKey, entry);
      const release = (committedRecord) => {
        entry.committed = committedRecord || null;
        settle(entry.committed);
        if (chains.get(fenceKey) === entry) chains.delete(fenceKey);
      };
      const superseded = () => chains.has(fenceKey) && chains.get(fenceKey) !== entry;

      let toSend = stamped;
      if (prior) {
        const committed = await prior.settled;
        const basedOnOwnChain = !record.rowVersion || prior.own.has(record.rowVersion);
        if (committed?.rowVersion && basedOnOwnChain) {
          toSend = { ...stamped, rowVersion: committed.rowVersion };
          entry.own.add(committed.rowVersion);
        }
      }

      const reconcile = (r) => {
        markRecordCommitted(fenceKey);
        const serverRecord = r?.[bodyKey];
        if (serverRecord?.rowVersion) entry.own.add(serverRecord.rowVersion);
        release(serverRecord);
        if (!serverRecord) return;
        const keepLaterEdit = superseded();
        updateState((s) => {
          const idx = (s[stateKey] || []).findIndex((x) => x && x.id === serverRecord.id);
          // A later save of this record is still in flight: the screen already
          // shows its content, which includes this one's. Keep that, and take
          // only the version this write committed, so the later save's
          // reconcile — not this one — decides what the record finally reads.
          if (idx >= 0 && keepLaterEdit) {
            s[stateKey][idx] = { ...s[stateKey][idx], rowVersion: serverRecord.rowVersion };
          } else if (idx >= 0) s[stateKey][idx] = serverRecord;
          else s[stateKey] = [serverRecord, ...(s[stateKey] || [])];
        });
      };

      const logSave = () => {
        if (!session) return;
        logActivity(tenantId, session, {
          module: 'Incidents',
          actionType: isEdit ? 'edited' : 'created',
          recordId: stamped.id,
          recordLabel: labelOf(stamped),
        });
      };

      let result;
      try {
        result = await saveFn(toSend, tenantId, options);
      } catch (e) {
        release(null);
        // A refused write must never leave the optimistic record on screen.
        // An incident register that shows an incident the server never stored
        // is TRYGGR_INTENT §6.2 in its most direct form — and for this module
        // the record is also the ISO 27001 / GDPR Art. 33 evidence that the
        // event was handled at all.
        updateState((s) => {
          // The fence skips the rollback when a write to this record committed
          // after this one began — which, in a chain, is this tab's own
          // previous save. Fall back to what that save committed, so the
          // screen shows the record as stored rather than this refused edit.
          if (prior?.committed && !superseded() && committedSinceFence(fence)) {
            const idx = (s[stateKey] || []).findIndex((x) => x && x.id === stamped.id);
            if (idx >= 0) s[stateKey][idx] = prior.committed;
            return;
          }
          s[stateKey] = restoreRecord(s[stateKey], stamped.id, before, { fence });
        });
        const queued = queueNetworkWrite({
          key: fenceKey,
          error: e,
          attempt: () => saveFn(toSend, tenantId),
          onSuccess: (r) => {
            reconcile(r || { [bodyKey]: toSend });
            if (onSaved) onSaved(stamped);
            logSave();
          },
        });
        e.willRetry = queued;
        throw e;
      }
      reconcile(result);
      if (onSaved) onSaved(stamped);
      logSave();
      return result;
    };

    const del = async (id, label, session) => {
      let before = null;
      const fenceKey = `${stateKey}:${id}`;
      const fence = beginRecordWrite(fenceKey);
      updateState((s) => {
        before = captureRecord(s[stateKey], id);
        s[stateKey] = (s[stateKey] || []).filter((r) => !r || r.id !== id);
      });

      try {
        await deleteFn(id, tenantId);
      } catch (e) {
        updateState((s) => {
          s[stateKey] = restoreRecord(s[stateKey], id, before, { fence });
        });
        throw e;
      }
      markRecordCommitted(fenceKey);

      if (onDeleted) onDeleted(id);

      if (session) {
        logActivity(tenantId, session, {
          module: 'Incidents',
          actionType: 'deleted',
          recordId: id,
          recordLabel: label || id,
        });
      }
    };

    return { save, del };
  }

  const incident = makeCollectionActions({
    stateKey: 'incidents',
    bodyKey: 'incident',
    saveFn: incidentApi.saveIncidentRecord,
    deleteFn: incidentApi.deleteIncidentRecord,
    labelOf: (r) => r.title,
    onSaved: (r) => emitEngineEvent('incident.saved', { incident: r }),
    onDeleted: (id) => emitEngineEvent('incident.deleted', { incidentId: id }),
  });

  const exercise = makeCollectionActions({
    stateKey: 'incidentExercises',
    bodyKey: 'exercise',
    saveFn: incidentApi.saveIncidentExerciseRecord,
    deleteFn: incidentApi.deleteIncidentExerciseRecord,
    labelOf: (r) => `Exercise: ${r.title}`,
  });

  const saveIncident = useCallback(incident.save, [updateState, tenantId]);
  const deleteIncident = useCallback(incident.del, [updateState, tenantId]);
  const saveIncidentExercise = useCallback(exercise.save, [updateState, tenantId]);
  const deleteIncidentExercise = useCallback(exercise.del, [updateState, tenantId]);

  return {
    saveIncident,
    deleteIncident,
    saveIncidentExercise,
    deleteIncidentExercise,
  };
}
